/**
 * BULK ACTIONS BAR
 * Barre d'actions de masse fixée en bas de l'écran
 * Affichée lorsque des éléments sont sélectionnés
 */

import { CalendarPlus, Download, Mail, Trash2, UserPlus, X } from 'lucide-react';

interface BulkActionsBarProps {
  count: number;
  entityNamePlural: string; // "produits", "prospects", etc.
  onDelete: () => void;
  onExportCSV: () => void;
  onEmail?: () => void; // optionnel
  onAssign?: () => void; // optionnel - pour assigner un utilisateur en masse
  onCreateTask?: () => void; // optionnel - pour créer une relance/tâche en masse (item #16)
  onClearSelection: () => void;
  showEmailAction?: boolean; // par défaut false
  showAssignAction?: boolean; // par défaut false
  showCreateTaskAction?: boolean; // par défaut false
  canDelete?: boolean; // Permission de supprimer (par défaut: true)
  canExport?: boolean; // Permission d'exporter (par défaut: true)
  /** 00253 (retour 30) : export des réponses du bulletin d'engagement (équipages). */
  onExportBedAnswers?: () => void;
}

export function BulkActionsBar({
  count,
  entityNamePlural,
  onDelete,
  onExportCSV,
  onEmail,
  onAssign,
  onCreateTask,
  onClearSelection,
  showEmailAction = false,
  showAssignAction = false,
  showCreateTaskAction = false,
  canDelete = true,
  canExport = true,
  onExportBedAnswers,
}: BulkActionsBarProps) {
  if (count === 0) return null;

  return (
    <div className="fixed bottom-16 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom duration-300">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl px-6 py-3">
        <div className="flex items-center gap-6">
          {/* Compteur + Boutons d'actions */}
          <div className="flex items-center gap-4">
            {/* Compteur */}
            <div className="flex items-center gap-2">
              <button
                onClick={onClearSelection}
                className="flex items-center justify-center size-8 rounded-md hover:bg-zinc-800 transition-colors"
                aria-label="Désélectionner tout"
                title="Désélectionner tout"
              >
                <X className="size-4 text-white" />
              </button>
              <span className="text-sm font-medium text-white">
                {count} {entityNamePlural}
              </span>
            </div>

            {/* Séparateur (uniquement si au moins une action est visible) */}
            {(canDelete ||
              canExport ||
              showEmailAction ||
              showAssignAction ||
              showCreateTaskAction) && <div className="h-6 w-px bg-zinc-700" />}

            {/* Bouton Supprimer (conditionnel selon permission) */}
            {canDelete && (
              <button
                onClick={onDelete}
                className="flex items-center gap-2 h-9 px-4 rounded-md bg-zinc-800 hover:bg-red-600 transition-colors"
                aria-label={`Supprimer ${count} élément${count > 1 ? 's' : ''}`}
                title={`Supprimer ${count} élément${count > 1 ? 's' : ''}`}
              >
                <Trash2 className="size-4 text-white" />
                <span className="text-sm font-medium text-white">Supprimer</span>
              </button>
            )}

            {/* Bouton Export CSV (conditionnel selon permission) */}
            {canExport && (
              <button
                onClick={onExportCSV}
                className="flex items-center gap-2 h-9 px-4 rounded-md bg-zinc-800 hover:bg-zinc-700 transition-colors"
                aria-label={`Exporter ${count} élément${count > 1 ? 's' : ''} en CSV`}
                title={`Exporter ${count} élément${count > 1 ? 's' : ''} en CSV`}
              >
                <Download className="size-4 text-white" />
                <span className="text-sm font-medium text-white">Export CSV</span>
              </button>
            )}

            {/* 00253 (retour 30) : réponses du bulletin d'engagement */}
            {canExport && onExportBedAnswers && (
              <button
                onClick={onExportBedAnswers}
                className="flex items-center gap-2 h-9 px-4 rounded-md bg-zinc-800 hover:bg-zinc-700 transition-colors"
                aria-label="Exporter les réponses du bulletin d'engagement en CSV"
                title="Exporter les réponses du bulletin d'engagement (BED) en CSV"
              >
                <Download className="size-4 text-white" />
                <span className="text-sm font-medium text-white">Réponses BED</span>
              </button>
            )}

            {/* Bouton Email (conditionnel) */}
            {showEmailAction && (
              <button
                onClick={onEmail}
                className="flex items-center gap-2 h-9 px-4 rounded-md bg-zinc-800 hover:bg-zinc-700 transition-colors"
                aria-label={`Envoyer un email à ${count} élément${count > 1 ? 's' : ''}`}
                title={`Envoyer un email à ${count} élément${count > 1 ? 's' : ''}`}
              >
                <Mail className="size-4 text-white" />
                <span className="text-sm font-medium text-white">Envoyer un email</span>
              </button>
            )}

            {/* Bouton Assigner (conditionnel) */}
            {showAssignAction && (
              <button
                onClick={onAssign}
                className="flex items-center gap-2 h-9 px-4 rounded-md bg-zinc-800 hover:bg-zinc-700 transition-colors"
                aria-label={`Assigner ${count} élément${count > 1 ? 's' : ''} à un utilisateur`}
                title={`Assigner ${count} élément${count > 1 ? 's' : ''} à un utilisateur`}
              >
                <UserPlus className="size-4 text-white" />
                <span className="text-sm font-medium text-white">Assigner</span>
              </button>
            )}

            {/* Bouton Créer une relance / tâche en masse (item #16) */}
            {showCreateTaskAction && (
              <button
                onClick={onCreateTask}
                className="flex items-center gap-2 h-9 px-4 rounded-md bg-zinc-800 hover:bg-zinc-700 transition-colors"
                aria-label={`Créer une relance pour ${count} élément${count > 1 ? 's' : ''}`}
                title={`Créer une relance pour ${count} élément${count > 1 ? 's' : ''}`}
              >
                <CalendarPlus className="size-4 text-white" />
                <span className="text-sm font-medium text-white">Créer une relance</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
